import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { getMdaClaimRuleBreaks } from "./mda-service";
import { MdaClaim, MdaClaimRuleBreak } from "./mda-types";

interface MdaClaimRuleBreaksDrawerProps {
  claim: MdaClaim | null;
  onClose: () => void;
  // Where the claim sits in the list being stepped through (zero-based).
  position?: { index: number; total: number };
  // Omitted at either end of the list, which disables that button.
  onPrevious?: () => void;
  onNext?: () => void;
}

const TITLE_ID = "mda-claim-rule-breaks-title";

const MdaClaimRuleBreaksDrawer = ({
  claim,
  onClose,
  position,
  onPrevious,
  onNext,
}: MdaClaimRuleBreaksDrawerProps) => {
  const [ruleBreaks, setRuleBreaks] = useState<MdaClaimRuleBreak[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const entityId = claim?.entityId;

  useEffect(() => {
    if (!entityId) return undefined;

    // Ignore a slow response for a claim the user has already moved away from.
    let isCurrent = true;
    setRuleBreaks([]);
    setHasError(false);
    setLoading(true);

    getMdaClaimRuleBreaks(entityId)
      .then((result) => {
        if (isCurrent) setRuleBreaks(result);
      })
      .catch(() => {
        if (isCurrent) setHasError(true);
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [entityId]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} aria-label="Loading rule breaks" />
        </Box>
      );
    }

    if (hasError) {
      return (
        <Alert severity="error">
          Unable to load the rule breaks for this claim. Please try again.
        </Alert>
      );
    }

    if (!ruleBreaks.length) {
      return (
        <Typography sx={{ color: "#66758b", py: 2 }}>
          No rule breaks recorded for this claim.
        </Typography>
      );
    }

    return (
      <List
        aria-label="Rule breaks"
        disablePadding
        sx={{ border: "1px solid #dfe6f0", borderRadius: 1.5 }}
      >
        {ruleBreaks.map((ruleBreak, index) => (
          <ListItem
            key={ruleBreak.id}
            divider={index < ruleBreaks.length - 1}
            sx={{ alignItems: "flex-start" }}
          >
            <ListItemText
              primary={ruleBreak.details}
              secondary={ruleBreak.involvement}
              slotProps={{
                primary: { sx: { color: "#1c2d49", whiteSpace: "pre-wrap" } },
                secondary: { sx: { color: "#66758b", mt: 0.5 } },
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(claim)}
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-labelledby": TITLE_ID,
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100%",
          // Sit below the app bar, as the other side panels do.
          mt: 8,
          height: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          px: 3,
          py: 2,
          borderBottom: "1px solid #dfe6f0",
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id={TITLE_ID}
            component="h2"
            sx={{ color: "#12233f", fontSize: "1.15rem", fontWeight: 700 }}
          >
            Rule breaks
          </Typography>
          <Typography
            sx={{ color: "#66758b", fontSize: "0.9rem", mt: 0.5 }}
            noWrap
          >
            {claim?.claimReference}
            {claim?.clientName ? ` · ${claim.clientName}` : ""}
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label="Close rule breaks"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
        {renderContent()}
      </Box>
      {position && position.total > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 3,
            py: 1.5,
            borderTop: "1px solid #dfe6f0",
            flexShrink: 0,
          }}
        >
          <Button
            onClick={onPrevious}
            disabled={!onPrevious}
            startIcon={<ChevronLeftIcon />}
            sx={{ color: "#1769ff", fontWeight: 600, textTransform: "none" }}
          >
            Previous
          </Button>
          <Typography sx={{ color: "#66758b", fontSize: "0.875rem" }}>
            {position.index + 1} of {position.total}
          </Typography>
          <Button
            onClick={onNext}
            disabled={!onNext}
            endIcon={<ChevronRightIcon />}
            sx={{ color: "#1769ff", fontWeight: 600, textTransform: "none" }}
          >
            Next
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default MdaClaimRuleBreaksDrawer;
